import { takeLatest, call, put, all, select } from 'redux-saga/effects'
import { normalize } from 'normalizr'
import { push as pushHistory } from 'react-router-redux'
import { setActiveLanguage } from 'react-localize-redux'

import actions, { constants } from '../actions/auth'
import systemsActions from '../actions/systems'
import snackbarActions from 'common/actions/snackbar'
import * as schemas from 'schemas'
import * as api from 'api/auth'
import * as userApi from 'api/user'
import * as Routes from 'constants/Routes'
import { setTokenHeader, clearTokenHeader } from 'utils/request'
import { getAuthStoreData, setAuthStoreData, clearAuthStoreData } from 'utils/authStore'
import * as LocalizeStore from 'utils/localizeStore'
import { setUserMeasurementSystem } from 'utils/auxStore'

function* postAuth(routing) {
  if (routing.locationBeforeTransitions.pathname === Routes.MAIN) {
    return yield put(pushHistory(Routes.PROJECTS))
  }
  yield put(pushHistory(routing.locationBeforeTransitions))
}

/**
 * Reads the username claim out of the session tokens.
 *
 * @param {Object} tokens - Tokens from getCognitoSession.
 * @returns {string|undefined} The username, or undefined when there is none.
 */
const getUsernameFromTokens = tokens => tokens?.accessToken?.payload?.username

/**
 * Reads the raw access token out of the session tokens.
 *
 * In Amplify v6 accessToken.toString() gives the JWT and accessToken.payload
 * gives the claims. In v4 these came off a session object instead.
 *
 * @param {Object} tokens - Tokens from getCognitoSession.
 * @returns {string|undefined} The JWT, or undefined when there is none.
 */
const getJwtFromTokens = tokens => tokens?.accessToken?.toString()

export function* onAuthUser() {
  yield put(actions.authUserRequest.start())
  const routing = yield select(state => state.routing)
  // Pages to ignore
  if (
    routing.locationBeforeTransitions.pathname.includes(Routes.CHANNEL) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.LOGIN) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.FULLSCREEN_LINK) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.FORGOTTEN_PASSWORD) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.CONFIRM_USER) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.RECOVER_PASSWORD) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.CONFIRM_RECIPIENT) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.CONFIRM_TRUSTED_USER) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.CHECK_TRUSTED_USER_STATUS) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.SIGN_UP_TRUSTED_USER) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.POLICY) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.TERMS) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.COOKIES) ||
    routing.locationBeforeTransitions.pathname.includes(Routes.MANAGE_COOKIES)
  ) {
    return yield put(actions.authUserRequest.failure(null))
  }

  const authStoreData = getAuthStoreData()

  if (!authStoreData) {
    yield put(actions.authUserRequest.failure())

    return yield call(postAuth, routing)
  }
  try {
    const tokens = yield call(api.getCognitoSession)
    setTokenHeader(authStoreData)
    const { user } = yield call(userApi.getCognitoUser, {
      username: getUsernameFromTokens(tokens)
    }) // Pedimos el usuario de la Dynamo
    setUserMeasurementSystem(user.measurementSystem || 'mS')
    yield put(setActiveLanguage(user.language))
    yield call(LocalizeStore.setAuthStoreData, user.language)
    const norm = yield call(normalize, user, schemas.user)
    yield put(actions.authUserRequest.success(norm))
    yield call(postAuth, routing)
  } catch (err) {
    // eslint-disable-next-line no-console
    // if (__DEV__) console.log({ err })
    yield put(actions.authUserRequest.failure(err))
    yield put(pushHistory(Routes.LOGIN))
    yield put(snackbarActions.showSnackbar('snackbar.expiredToken'))
  }
}

export function* onLoginCognito({ payload: data }) {
  yield put(actions.loginRequest.start())

  try {
    const { isSignedIn, nextStep } = yield call(api.loginCognito, data)

    // v4 threw UserNotConfirmedException for an unconfirmed account. v6 does not
    // throw: it reports CONFIRM_SIGN_UP as the next step. We pass that on as a
    // needsConfirmation flag so LoginForm can offer to resend the code. Any
    // other unfinished step is one we do not support, so it stays an error.
    if (!isSignedIn) {
      yield put(
        actions.loginRequest.failure(
          nextStep?.signInStep === 'CONFIRM_SIGN_UP' ? { needsConfirmation: true } : { nextStep }
        )
      )
    } else {
      const tokens = yield call(api.getCognitoSession)
      const jwt = getJwtFromTokens(tokens)

      yield call(setAuthStoreData, 'Bearer ' + jwt)
      yield call(setTokenHeader, 'Bearer ' + jwt)

      const { user } = yield call(userApi.getCognitoUser, {
        username: getUsernameFromTokens(tokens)
      }) // Pedimos el usuario de la Dynamo
      setUserMeasurementSystem(user.measurementSystem || 'mS')
      yield put(setActiveLanguage(user.language))
      yield call(LocalizeStore.setAuthStoreData, user.language)
      const norm = yield call(normalize, user, schemas.user)

      yield put(actions.loginRequest.success(norm))
      yield put(pushHistory(Routes.PROJECTS))
    }
  } catch (err) {
    yield put(actions.loginRequest.failure(err))
  }
}

export function* onSignUpTrustedUserCognito({ payload: data }) {
  yield put(actions.signUpTrustedUserRequest.start())

  try {
    // v6 signUpCognito throws when the account cannot be created, so reaching
    // the next line means Cognito accepted it.
    yield call(api.signUpCognito, data)
    yield call(api.signUpTrustedUser, data) // Si el registo en cognito ha sido correcto, lo metemos también en Dynamo DB
    yield put(actions.signUpTrustedUserRequest.success())

    yield put(snackbarActions.showSnackbar('snackbar.confirmSignUpTrustedUserSuccess'))

    yield put(
      pushHistory({
        pathname:
          Routes.CONFIRM_TRUSTED_USER +
          '/' +
          data.email +
          '/' +
          data.mainEmail +
          '/' +
          data.projectId
      })
    )
  } catch (err) {
    // eslint-disable-next-line no-console
    // if (__DEV__) console.log({ err })

    yield put(actions.signUpTrustedUserRequest.failure(err))
  }
}

export function* onSignUpCognito({ payload: data }) {
  yield put(actions.signUpRequest.start())

  try {
    // v6 signUpCognito throws when the account cannot be created, so reaching
    // the next line means Cognito accepted it. It no longer returns the user,
    // and in this pool the username is the email, so we pass on what was typed.
    yield call(api.signUpCognito, data)
    yield call(api.signUp, data) // Si el registo en cognito ha sido correcto, lo metemos también en Dynamo DB
    yield put(actions.signUpRequest.success())
    yield put(snackbarActions.showSnackbar('snackbar.confirmationCodeSent'))
    yield put(
      pushHistory({
        pathname: Routes.CONFIRM_USER_INFO,
        state: { username: data.email }
      })
    )
  } catch (err) {
    // eslint-disable-next-line no-console
    // if (__DEV__) console.log({ err })

    yield put(actions.signUpRequest.failure(err))
  }
}

export function* onConfirmUser({ payload: data }) {
  yield put(actions.confirmUserRequest.start())
  try {
    // v6 confirmSignUp throws on a bad or expired code, so reaching the next
    // line means the account is confirmed.
    yield call(api.confirmSignUpCognito, data)
    yield put(actions.confirmUserRequest.success())
    yield put(snackbarActions.showSnackbar('snackbar.confirmSignUpSuccess'))
  } catch (err) {
    // eslint-disable-next-line no-console
    // if (__DEV__) console.log({ err })
    yield put(actions.confirmUserRequest.failure(err))
  }
}

export function* onResendConfirmationMail({ payload: data }) {
  yield put(actions.resendConfirmationMailRequest.start())
  try {
    // v6 resendSignUpCode throws when the code cannot be sent, so reaching the
    // next line means it went out.
    yield call(api.resendConfirmationMail, data.username)
    yield put(actions.resendConfirmationMailRequest.success())
    yield put(snackbarActions.showSnackbar('snackbar.resendConfirmationMailSuccess'))
  } catch (err) {
    yield put(actions.resendConfirmationMailRequest.failure(err))
    yield put(snackbarActions.showSnackbar('snackbar.resendConfirmationMailFailure'))
  }
}

export function* onLogoutCognito() {
  yield put(systemsActions.pollSystemsStop())
  try {
    yield call(api.logoutCognito)
  } catch (err) {
    // Signing out of Cognito can fail, for example on a bad Cognito config. We
    // swallow it on purpose: the local session is cleared below either way, and
    // an error escaping here would take down every saga watcher with it, so the
    // login page would stop responding until the user reloads.
  } finally {
    // Always clear the local session, even if Cognito could not be reached.
    // Otherwise a failed signOut would leave the user stuck signed in here.
    yield call(clearAuthStoreData)
    yield call(clearTokenHeader)
    yield put(pushHistory(Routes.LOGIN))
  }
}

export default function* watchAuth() {
  yield all([
    takeLatest(constants.AUTH_USER, onAuthUser),
    takeLatest(constants.LOGIN, onLoginCognito),
    takeLatest(constants.SIGN_UP, onSignUpCognito),
    takeLatest(constants.SIGN_UP_TRUSTED_USER, onSignUpTrustedUserCognito),
    takeLatest(constants.CONFIRM_USER, onConfirmUser),
    takeLatest(constants.RESEND_CONFIRMATION_MAIL, onResendConfirmationMail),
    takeLatest(constants.LOGOUT, onLogoutCognito)
  ])
}
